import { useState, useEffect, useCallback } from 'react';
import { obterAtividadeRecente, type AlteracaoRegisto, type TipoAcao, type EntidadeAuditada } from '@/utils/auditoria';

const ENTIDADE_CONFIG: Record<EntidadeAuditada, { icon: string; color: string; label: string }> = {
  funcionario: { icon: 'ri-user-line', color: 'text-zinc-600', label: 'Funcionário' },
  formacao: { icon: 'ri-book-open-line', color: 'text-teal-600', label: 'Formação' },
  participante: { icon: 'ri-user-follow-line', color: 'text-sky-600', label: 'Participante' },
  documento: { icon: 'ri-file-text-line', color: 'text-amber-600', label: 'Documento' },
  formador: { icon: 'ri-user-star-line', color: 'text-violet-600', label: 'Formador' },
  utilizador: { icon: 'ri-team-line', color: 'text-zinc-500', label: 'Utilizador' },
  unidade: { icon: 'ri-building-2-line', color: 'text-zinc-500', label: 'Unidade' },
  carreira: { icon: 'ri-briefcase-line', color: 'text-zinc-500', label: 'Carreira' },
};

const ACAO_CONFIG: Record<TipoAcao, { label: string; bg: string; text: string; dot: string; icon: string }> = {
  criacao: { label: 'Criação', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', icon: 'ri-add-circle-line' },
  edicao: { label: 'Edição', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500', icon: 'ri-edit-line' },
  eliminacao: { label: 'Eliminação', bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500', icon: 'ri-delete-bin-line' },
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'agora mesmo';
  if (mins < 60) return `há ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `há ${hours}h`;
  const days = Math.floor(hours / 24);
  return `há ${days}d`;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
}

interface Anomalia {
  id: string;
  tipo: 'eliminacao_multipla' | 'volume_alto' | 'fora_horario' | 'formacao_com_participantes' | 'funcionario_com_avaliacoes';
  severidade: 'alta' | 'media' | 'baixa';
  titulo: string;
  descricao: string;
  icon: string;
  bg: string;
  border: string;
  text: string;
}

function detetarAnomalias(registos: AlteracaoRegisto[]): Anomalia[] {
  const anomalias: Anomalia[] = [];
  const agora = Date.now();
  const h24 = 24 * 60 * 60 * 1000;
  const h1 = 60 * 60 * 1000;

  // 1. Eliminações múltiplas nas últimas 24h
  const eliminacoes24h = registos.filter(
    (r) => r.tipo_acao === 'eliminacao' && agora - new Date(r.data_alteracao).getTime() < h24
  );
  if (eliminacoes24h.length >= 3) {
    anomalias.push({
      id: 'elim-multipla',
      tipo: 'eliminacao_multipla',
      severidade: 'alta',
      titulo: `${eliminacoes24h.length} eliminações nas últimas 24h`,
      descricao: 'Foram registadas múltiplas eliminações num curto espaço de tempo. Verifica se são intencionais.',
      icon: 'ri-delete-bin-2-line',
      bg: 'bg-red-50',
      border: 'border-red-100',
      text: 'text-red-700',
    });
  }

  // 2. Volume alto de edições na mesma hora
  const edicoes1h = registos.filter(
    (r) => r.tipo_acao === 'edicao' && agora - new Date(r.data_alteracao).getTime() < h1
  );
  if (edicoes1h.length >= 10) {
    anomalias.push({
      id: 'volume-alto',
      tipo: 'volume_alto',
      severidade: 'media',
      titulo: `${edicoes1h.length} edições na última hora`,
      descricao: 'Volume atípico de alterações. Pode indicar uma atualização em massa ou atividade suspeita.',
      icon: 'ri-flashlight-line',
      bg: 'bg-amber-50',
      border: 'border-amber-100',
      text: 'text-amber-700',
    });
  }

  // 3. Alterações fora do horário normal (antes 8h ou depois 20h)
  const foraHorario = registos.filter((r) => {
    const d = new Date(r.data_alteracao);
    const hora = d.getHours();
    return hora < 8 || hora >= 20;
  });
  if (foraHorario.length >= 3) {
    anomalias.push({
      id: 'fora-horario',
      tipo: 'fora_horario',
      severidade: 'baixa',
      titulo: `${foraHorario.length} alterações fora do horário de trabalho`,
      descricao: 'Detetada atividade administrativa durante horário atípico.',
      icon: 'ri-moon-line',
      bg: 'bg-sky-50',
      border: 'border-sky-100',
      text: 'text-sky-700',
    });
  }

  // 4. Formação eliminada com participantes inscritos
  const formacoesEliminadas = registos.filter(
    (r) => r.entidade === 'formacao' && r.tipo_acao === 'eliminacao'
  );
  for (const f of formacoesEliminadas) {
    if (f.descricao.includes('participantes')) {
      anomalias.push({
        id: `form-part-${f.id}`,
        tipo: 'formacao_com_participantes',
        severidade: 'alta',
        titulo: 'Formação eliminada com participantes',
        descricao: `"${f.entidade_nome ?? f.entidade_id}" tinha participantes inscritos quando foi eliminada.`,
        icon: 'ri-alert-line',
        bg: 'bg-red-50',
        border: 'border-red-100',
        text: 'text-red-700',
      });
    }
  }

  // 5. Funcionário eliminado com avaliações
  const funcsEliminados = registos.filter(
    (r) => r.entidade === 'funcionario' && r.tipo_acao === 'eliminacao'
  );
  if (funcsEliminados.length > 0) {
    anomalias.push({
      id: 'func-avaliacoes',
      tipo: 'funcionario_com_avaliacoes',
      severidade: 'media',
      titulo: `${funcsEliminados.length} funcionário(s) eliminado(s)`,
      descricao: 'Verifica se os registos SIADAP associados foram também tratados.',
      icon: 'ri-user-forbid-line',
      bg: 'bg-orange-50',
      border: 'border-orange-100',
      text: 'text-orange-700',
    });
  }

  return anomalias;
}

const PAGE_SIZE = 15;

export default function AtividadeRecenteCard() {
  const [registos, setRegistos] = useState<AlteracaoRegisto[]>([]);
  const [loading, setLoading] = useState(true);
  const [entidadeFilter, setEntidadeFilter] = useState<EntidadeAuditada | 'all'>('all');
  const [acaoFilter, setAcaoFilter] = useState<TipoAcao | 'all'>('all');
  const [page, setPage] = useState(1);
  const [anomalias, setAnomalias] = useState<Anomalia[]>([]);

  const loadAtividade = useCallback(async () => {
    setLoading(true);
    const data = await obterAtividadeRecente(100, 7);
    setRegistos(data);
    setAnomalias(detetarAnomalias(data));
    setLoading(false);
  }, []);

  useEffect(() => {
    loadAtividade();
  }, [loadAtividade]);

  useEffect(() => { setPage(1); }, [entidadeFilter, acaoFilter]);

  const filtered = registos.filter((r) => {
    const entidade = r.entidade ?? 'funcionario';
    const matchEntidade = entidadeFilter === 'all' || entidade === entidadeFilter;
    const matchAcao = acaoFilter === 'all' || r.tipo_acao === acaoFilter;
    return matchEntidade && matchAcao;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function exportCSV() {
    const headers = ['Data/Hora', 'Entidade', 'Nome', 'Ação', 'Campo Alterado', 'Valor Anterior', 'Novo Valor', 'Descrição', 'Módulo', 'Utilizador'];
    const rows = filtered.map((r) => [
      formatDateTime(r.data_alteracao),
      r.entidade ?? 'funcionario',
      r.entidade_nome || r.entidade_id || '—',
      ACAO_CONFIG[r.tipo_acao].label,
      r.campo_alterado ?? '',
      r.valor_anterior ?? '',
      r.valor_novo ?? '',
      r.descricao,
      r.modulo ?? '',
      r.user_email ?? '',
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(';'))
      .join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `atividade-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const entidadesDisponiveis = Array.from(new Set(registos.map((r) => r.entidade ?? 'funcionario')));

  return (
    <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-zinc-100 rounded-lg flex-shrink-0">
            <i className="ri-radar-line text-zinc-600 text-sm" />
          </div>
          <div>
            <h2 className="text-[14px] font-semibold text-zinc-800">Atividade Recente</h2>
            <p className="text-[11px] text-zinc-400 mt-0.5">Alterações dos últimos 7 dias de todos os módulos</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadAtividade}
            disabled={loading}
            className="whitespace-nowrap flex items-center gap-1.5 text-[12px] font-medium text-zinc-600 border border-zinc-200 bg-white hover:bg-zinc-50 px-3 py-2 rounded-lg transition-colors cursor-pointer disabled:opacity-50"
          >
            <i className={`ri-refresh-line text-sm ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
          <button
            onClick={exportCSV}
            disabled={filtered.length === 0}
            className="whitespace-nowrap flex items-center gap-1.5 text-[12px] font-medium text-zinc-500 hover:text-zinc-800 border border-zinc-200 bg-white hover:bg-zinc-50 px-3 py-2 rounded-lg transition-colors cursor-pointer disabled:opacity-40"
          >
            <i className="ri-download-2-line text-sm" />
            Exportar CSV
          </button>
        </div>
      </div>

      {/* Alertas de anomalias */}
      {anomalias.length > 0 && (
        <div className="px-6 pt-4 pb-0">
          <div className="space-y-2 mb-4">
            {anomalias.map((a) => (
              <div key={a.id} className={`flex items-start gap-3 p-3 rounded-xl border ${a.bg} ${a.border}`}>
                <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 bg-white/60">
                  <i className={`${a.icon} ${a.text} text-sm`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className={`text-[13px] font-semibold ${a.text}`}>{a.titulo}</p>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wider ${
                      a.severidade === 'alta' ? 'bg-red-100 text-red-600' :
                      a.severidade === 'media' ? 'bg-amber-100 text-amber-600' :
                      'bg-sky-100 text-sky-600'
                    }`}>
                      {a.severidade}
                    </span>
                  </div>
                  <p className="text-[11px] text-zinc-500 mt-0.5 leading-relaxed">{a.descricao}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="px-6 py-3 border-b border-zinc-50 flex items-center gap-3 flex-wrap">
        {/* Filtro entidade */}
        <div className="flex items-center gap-1 bg-zinc-100 rounded-lg p-1">
          <button
            onClick={() => setEntidadeFilter('all')}
            className={`whitespace-nowrap px-3 py-1 text-[12px] font-medium rounded-md transition-all cursor-pointer ${
              entidadeFilter === 'all' ? 'bg-white text-zinc-800 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
            }`}
          >
            Todas
          </button>
          {entidadesDisponiveis.map((e) => {
            const cfg = ENTIDADE_CONFIG[e as EntidadeAuditada] ?? ENTIDADE_CONFIG.funcionario;
            return (
              <button
                key={e}
                onClick={() => setEntidadeFilter(e as EntidadeAuditada)}
                className={`whitespace-nowrap px-3 py-1 text-[12px] font-medium rounded-md transition-all cursor-pointer flex items-center gap-1 ${
                  entidadeFilter === e ? 'bg-white text-zinc-800 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
                }`}
              >
                <i className={`${cfg.icon} text-xs`} />
                {cfg.label}
              </button>
            );
          })}
        </div>

        {/* Filtro ação */}
        <div className="flex items-center gap-1 bg-zinc-100 rounded-lg p-1">
          <button
            onClick={() => setAcaoFilter('all')}
            className={`whitespace-nowrap px-3 py-1 text-[12px] font-medium rounded-md transition-all cursor-pointer ${
              acaoFilter === 'all' ? 'bg-white text-zinc-800 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
            }`}
          >
            Todas
          </button>
          {(['criacao', 'edicao', 'eliminacao'] as TipoAcao[]).map((a) => (
            <button
              key={a}
              onClick={() => setAcaoFilter(a)}
              className={`whitespace-nowrap px-3 py-1 text-[12px] font-medium rounded-md transition-all cursor-pointer flex items-center gap-1 ${
                acaoFilter === a ? 'bg-white text-zinc-800 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
              }`}
            >
              <i className={`${ACAO_CONFIG[a].icon} text-xs`} />
              {ACAO_CONFIG[a].label}
            </button>
          ))}
        </div>

        <span className="text-[11px] text-zinc-400 flex-shrink-0 ml-auto">
          {filtered.length} registo{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Feed */}
      <div className="divide-y divide-zinc-50 max-h-[520px] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <i className="ri-loader-4-line text-2xl text-zinc-300 animate-spin" />
          </div>
        ) : paginated.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 gap-2 text-zinc-400">
            <div className="w-10 h-10 flex items-center justify-center bg-zinc-50 rounded-full">
              <i className="ri-radar-line text-xl" />
            </div>
            <p className="text-[13px]">Nenhuma atividade registada nos últimos 7 dias.</p>
          </div>
        ) : (
          paginated.map((r) => {
            const entidade = (r.entidade ?? 'funcionario') as EntidadeAuditada;
            const eCfg = ENTIDADE_CONFIG[entidade] ?? ENTIDADE_CONFIG.funcionario;
            const aCfg = ACAO_CONFIG[r.tipo_acao];
            const nome = r.entidade_nome || r.entidade_id || '—';
            const temDetalhe = r.campo_alterado || r.valor_anterior || r.valor_novo;

            return (
              <div key={r.id} className="flex items-start gap-3 px-6 py-3 hover:bg-zinc-50/60 transition-colors">
                <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${aCfg.bg}`}>
                  <i className={`${eCfg.icon} ${aCfg.text} text-sm`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[13px] font-medium text-zinc-800 truncate">{nome}</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${aCfg.bg} ${aCfg.text} ${aCfg.bg.replace('bg-', 'border-').replace('50', '100')}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${aCfg.dot}`} />
                      {aCfg.label}
                    </span>
                    {r.modulo && (
                      <span className="text-[10px] font-semibold text-zinc-400 bg-zinc-100 px-1.5 py-0.5 rounded-full uppercase">
                        {r.modulo}
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-zinc-500 mt-0.5">{r.descricao}</p>
                  {temDetalhe && (
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      {r.campo_alterado && (
                        <span className="text-[11px] font-medium text-zinc-600 bg-zinc-50 px-2 py-0.5 rounded">
                          {r.campo_alterado}
                        </span>
                      )}
                      {r.valor_anterior && (
                        <span className="text-[11px] text-red-600 bg-red-50 px-2 py-0.5 rounded line-through">
                          {r.valor_anterior}
                        </span>
                      )}
                      {r.valor_novo && (
                        <span className="text-[11px] text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded">
                          {r.valor_novo}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-[11px] text-zinc-400">{timeAgo(r.data_alteracao)}</span>
                    <span className="text-zinc-200">·</span>
                    <span className="text-[11px] text-zinc-400">{formatDateTime(r.data_alteracao)}</span>
                    {r.user_email && (
                      <>
                        <span className="text-zinc-200">·</span>
                        <span className="text-[11px] text-zinc-400">{r.user_email}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Paginação */}
      {totalPages > 1 && !loading && (
        <div className="flex items-center justify-between px-6 py-3 border-t border-zinc-100">
          <p className="text-[11px] text-zinc-400">
            Página {page} de {totalPages}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 disabled:opacity-30 transition-colors cursor-pointer"
            >
              <i className="ri-skip-back-line text-sm" />
            </button>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 disabled:opacity-30 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-sm" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
              .reduce<(number | '...')[]>((acc, p, idx, arr) => {
                if (idx > 0 && typeof arr[idx - 1] === 'number' && (p as number) - (arr[idx - 1] as number) > 1) {
                  acc.push('...');
                }
                acc.push(p);
                return acc;
              }, [])
              .map((p, idx) =>
                p === '...' ? (
                  <span key={`ellipsis-${idx}`} className="w-8 h-8 flex items-center justify-center text-xs text-zinc-400">…</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setPage(p as number)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-medium transition-colors cursor-pointer ${
                      page === p ? 'bg-zinc-900 text-white' : 'text-zinc-500 hover:bg-zinc-100'
                    }`}
                  >
                    {p}
                  </button>
                )
              )}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 disabled:opacity-30 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-sm" />
            </button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 disabled:opacity-30 transition-colors cursor-pointer"
            >
              <i className="ri-skip-forward-line text-sm" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}